import {
  useConsultantGeneralInterest_UpdateMutation,
  useGetConsultantGeneralInterestQuery,
  Workshift
} from "@bonliva-crm/graphql";
import { Box, IconButton, Typography } from "@material-ui/core";
import { useGlobalStore, useLocale, useSnackbars } from "@hooks";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeType } from "@theme";
import {
  EditFilledIcon,
  filterOptionDefault,
  Modal,
  MultiCollapsible,
  MultiSelect,
  TextField
} from "@bonliva-crm/ui";
import { useEffect, useMemo, useState } from "react";
import { ActionMeta } from "react-select5";
import { formatRegionLabel, getRegions } from "./utils";
import { isEqual } from "lodash";

type Option = {
  label: string;
  value: string;
};

type Props = {
  consultantId: string;
};

export function GeneralInterest({ consultantId }: Props) {
  const { showSuccess, showError } = useSnackbars();
  const { locale } = useLocale();
  const { cache } = useGlobalStore();
  const classes = useStyles();

  const { data, loading } = useGetConsultantGeneralInterestQuery({
    variables: { consultantId },
    fetchPolicy: "cache-first",
    onError: (error) => {
      showError(locale.ErrorOccurred);
      console.error(error.message);
    }
  });

  const generalInterest = data?.consultantById.generalInterest;
  const availableInCountries = data?.consultantById.AvailableForWorkInCountries;

  const initialRegions = generalInterest?.regions.map((x) => x) ?? [];
  const initialWorkshifts = generalInterest?.workshifts.map((x) => x) ?? [];
  const initialComment = generalInterest?.comment ?? "";

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedWorkshifts, setSelectedWorkshifts] = useState<Workshift[]>([]);
  const [comment, setComment] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRegions(initialRegions);
    setSelectedWorkshifts(initialWorkshifts);
    setComment(initialComment);
  }, [modalOpen]);

  const regions = useMemo(() => {
    return modalOpen
      ? getRegions(cache.regionsWithBonlivaCare, availableInCountries)
      : [];
  }, [modalOpen]);

  const [mutate] = useConsultantGeneralInterest_UpdateMutation({
    variables: {
      consultantId: consultantId,
      interest: {
        regions: selectedRegions,
        workshifts: selectedWorkshifts,
        comment: comment.trim()
      }
    },
    optimisticResponse: (vars) => ({
      updateConsultantGeneralInterest: {
        ...data.consultantById,
        generalInterest: {
          __typename: "ConsultantGeneralInterest",
          ...vars.interest
        }
      }
    }),
    update: () => {
      setModalOpen(false);
    },
    onCompleted: () => {
      showSuccess(locale.Saved);
    },
    onError: (error) => {
      showError(locale.ErrorOccurred);
      console.error(error.message);
    }
  });

  if (loading) {
    return null;
  }

  const onChange = (options: Option[], actionMeta: ActionMeta<Option>) => {
    const { action, option } = actionMeta;
    let finalRegions = options;

    if (action === "select-option") {
      const country = option.value.split(":")[0];
      if (option.value === `${country}:*`) {
        finalRegions = options.filter((x) => !x.value.startsWith(`${country}:`));
        finalRegions.push(option);
      }
    }

    setSelectedRegions(finalRegions.map((option) => option.value));
  };

  const onCollapse = (option: Option) => {
    const country = option.value.split(":")[0];
    if (collapsedGroups.some((x) => x === country)) {
      setCollapsedGroups([...collapsedGroups.filter((x) => x !== country)]);
    } else {
      collapsedGroups.push(country);
      setCollapsedGroups([...collapsedGroups]);
    }
  };

  const isOptionSelected = (option: Option) => {
    const country = option.value.split(":")[0];
    if (selectedRegions.some((region) => region === `${country}:*`)) {
      return true;
    }

    return selectedRegions.some((region) => region === option.value);
  };

  const isOptionDisabled = (option: Option) => {
    const country = option.value.split(":")[0];
    if (option.value === `${country}:*`) {
      return false;
    }

    return selectedRegions.some((region) => region === `${country}:*`);
  };

  const filterOption = (option: LabelValue, inputValue?: string) => {
    if (inputValue) {
      return filterOptionDefault(option, inputValue);
    }

    const country = option.value.split(":")[0];
    if (option.value === `${country}:*`) {
      return true;
    }

    return !collapsedGroups.some((x) => x === country);
  };

  const isCollapsible = (option: Option) => option.value.endsWith(":*");

  const isCollapsed = (option: Option) => {
    const country = option.value.split(":")[0];

    return collapsedGroups.some((x) => x === country);
  };

  const isDirty = () => {
    return (
      !isEqual(initialRegions, selectedRegions) ||
      !isEqual(initialWorkshifts, selectedWorkshifts) ||
      !isEqual(initialComment.trim(), comment.trim())
    );
  };

  const modalContent = (
    <Box className={classes.modalContainer}>
      <Box>
        <MultiCollapsible<Option>
          inputLabel={locale.GeneralInterestRegion}
          placeholder={null}
          options={regions}
          value={selectedRegions.map((region) => ({
            label: formatRegionLabel(region),
            value: region
          }))}
          isClearable={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          minMenuHeight={500}
          onChange={onChange}
          onCollapse={onCollapse}
          isOptionSelected={isOptionSelected}
          isOptionDisabled={isOptionDisabled}
          isCollapsible={isCollapsible}
          isCollapsed={isCollapsed}
          filterOption={filterOption}
          styleProps={{ menu: { showBorder: true }, control: { showBorder: true } }}
        />
      </Box>

      <Box>
        <MultiSelect<Option>
          inputLabel={locale.PreferredSchedule}
          placeholder={null}
          options={Object.keys(locale.workshifts).map((x) => {
            return {
              label: locale.workshifts[x],
              value: x
            };
          })}
          value={selectedWorkshifts.map((x) => {
            return {
              label: locale.workshifts[x],
              value: x
            };
          })}
          showDropDownIndicator={true}
          isClearable={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          onChange={(options) =>
            setSelectedWorkshifts(options.map((x) => x.value as Workshift))
          }
          styleProps={{ menu: { showBorder: true }, control: { showBorder: true } }}
          optionType={"checkbox"}
          showOptionDivider={false}
        />
      </Box>

      <Box>
        <TextField
          label={locale.Comment}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          multiline={true}
          inputProps={{ maxLength: 800, rows: 5 }}
          fullWidth={true}
          placeholder={locale.Comment}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <Box className={classes.root}>
        <Box className={classes.headingContainer}>
          <Box className={classes.headingBox}>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classes.sectionTitle}
            >
              {locale["Interested in"]}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setModalOpen(true)}
            style={{
              marginLeft: "auto",
              marginBottom: "auto",
              alignItems: "start",
              padding: "6px"
            }}
          >
            <EditFilledIcon />
          </IconButton>
        </Box>
        <Row
          label={locale.GeneralInterestRegion}
          value={initialRegions.map((x) => formatRegionLabel(x)).join(", ")}
        />
        <Row
          label={locale.PreferredSchedule}
          value={initialWorkshifts.map((x) => locale.workshifts[x]).join(", ")}
        />
        <Row label={locale.Comment} value={initialComment} />
      </Box>

      {modalOpen && (
        <Modal
          open={modalOpen}
          setOpen={setModalOpen}
          modalContent={modalContent}
          label={`${locale.Edit} ${locale["Interested in"].toLowerCase()}`}
          saveButtonDisabled={!isDirty()}
          onSubmit={() => mutate()}
          style={{ width: "50%", left: "25%" }}
          dimensions={{ minHeight: "auto", minWidth: "500px" }}
        />
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="h6" component="span">
        {label}:{" "}
      </Typography>
      {value || "-"}
    </Box>
  );
}

const useStyles = makeStyles((theme: ThemeType) => {
  return {
    root: {
      display: "flex",
      flexDirection: "column",
      gap: "0.625rem",
      padding: theme.spacing(2),
      backgroundColor: theme.palette.common.white
    },
    headingContainer: {
      display: "flex",
      flexDirection: "row",
      gap: "0.25rem"
    },
    headingBox: {
      display: "flex",
      alignItems: "center"
    },
    sectionTitle: {
      textTransform: "uppercase"
    },
    modalContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "2rem"
    },
    placeholder: {
      display: "flex",
      columnGap: "0.5rem",
      alignItems: "center"
    }
  };
});
